import { toYMD } from 'helpers/toYMD';
import api from './axiosInstance';
import { getCurrentUser } from './authService';

export async function getCatalogs(signal) {
  try {
    const response = await api.get('/clients/Catalogs', { signal });
    return response.data;
  } catch (error) {
    console.error('Error fetching catalogs:', error);
    throw error;
  }
}

export async function getClientById(id, signal) {
  try {
    const response = await api.get(`/clients/${id}`, { signal });
    return response.data;
  } catch (error) {
    console.error('Error fetching client detail:', error);
    throw error;
  }
}

export async function getClaveUnicaByClientId(id, signal) {
  try {
    const response = await api.get(`/clients/${id}/clave-unica`, { signal });
    return response.data;
  } catch (error) {
    console.error('Error fetching client clave única:', error);
    throw error;
  }
}

export async function getSummaryClientEvaluation(payload, signal) {
  try {
    const response = await api.post(
      '/Transcripciones/SummaryClientEvaluation',
      payload,
      { signal }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching client summary evaluation:', error);
    throw error;
  }
}

export async function getCallsPaged(clientId, page = 1, pageSize = 10, signal) {
  try {
    const response = await api.get(`/clients/${clientId}/calls`, {
      params: {
        page,
        pageSize,
      },
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching calls:', error);
    throw error;
  }
}

export async function getEmailById(clientId, emailId, signal) {
  try {
    const response = await api.get(`/clients/${clientId}/emails/${emailId}`, {
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching email:', error);
    throw error;
  }
}

export async function getEmailsPaged(
  clientId,
  page = 1,
  pageSize = 10,
  search = null,
  signal
) {
  try {
    const params = {
      page,
      pageSize,
    };

    if (search) {
      params.search = search;
    }

    const response = await api.get(`/clients/${clientId}/emails`, {
      params,
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching emails:', error);
    throw error;
  }
}

export async function createClient(clientData) {
  try {
    const payload = {
      name: clientData.fullName,
      identifier: clientData.identifier,
      claveUnica: clientData.secretKey,
      email: clientData.email,
      telephone: clientData.phone,
      observations: clientData.notes,
      idCountry: parseInt(clientData.nationality),
      idIdentityType: parseInt(clientData.entityType),
      idGenre: parseInt(clientData.gender),
      idMaritalStatus: parseInt(clientData.maritalStatus),
      userIdSac: parseInt(clientData.sacLawyer),
      branchId: 1,
    };

    const response = await api.post('/clients/CreateNew', payload);

    return {
      success: true,
      clientId: response.data.clientId,
      message: response.data.message || 'Cliente creado exitosamente',
    };
  } catch (error) {
    console.error('Error inesperado:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Error desconocido al crear cliente';
    return {
      success: false,
      message: errorMessage,
    };
  }
}

const toInt = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const getUpdaterId = (clientData) => {
  const currentUser = getCurrentUser?.();
  const currentUserId =
    currentUser?.id ??
    currentUser?.userId ??
    currentUser?.usuarioAsignadoId ??
    null;
  return toInt(clientData.updaterId ?? currentUserId ?? 0);
};

const normalizeActiveValue = (active) => {
  if (active === true) return 1;
  if (active === false) return 0;
  return toInt(active);
};

export async function updateClient(clientData) {
  try {
    const payload = {
      clientId: toInt(clientData.clientId),
      name: clientData.fullName,
      identifier: clientData.identifier,
      email: clientData.email,
      phone: clientData.phone,
      phone2: clientData.phone2 ?? '',
      observations: clientData.notes ?? '',
      address: clientData.address ?? '',
      claveUnica: clientData.secretKey ?? '',
      idCountry: toInt(clientData.nationality),
      idIdentityType: toInt(clientData.entityType),
      idMaritalStatus: toInt(clientData.maritalStatus),
      idBranchOffice: toInt(clientData.branchOffice),
      idGenre: toInt(clientData.gender),
      idRegion: toInt(clientData.region),
      updaterId: getUpdaterId(clientData),
      newSac: toInt(clientData.sacLawyer),
    };

    const response = await api.put('/clients/Update2', payload);

    return {
      success: true,
      clientId: response.data.clientId ?? payload.clientId,
      message: response.data.message || 'Cliente actualizado exitosamente',
    };
  } catch (error) {
    console.error('Error inesperado:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Error desconocido al actualizar cliente';
    return {
      success: false,
      message: errorMessage,
    };
  }
}

export async function updateClientActive(clientData) {
  try {
    const payload = {
      clientId: toInt(clientData.clientId),
      active: normalizeActiveValue(clientData.active),
      updaterId: getUpdaterId(clientData),
    };

    const response = await api.put('/Clients/Active', payload);
    return response.data;
  } catch (error) {
    console.error('Error updating client active status:', error);
    throw error;
  }
}

export async function getClientsPaged(params, signal) {
  try {
    const { page, pageSize, q, state, sortBy, desc, ...filters } = params;

    const queryObject = {
      page,
      pageSize,
    };

    if (q) queryObject.q = q;

    if (state === 'active') {
      queryObject.active = true;
    } else if (state === 'inactive') {
      queryObject.active = false;
    }

    if (sortBy) queryObject.sortBy = sortBy;
    if (desc !== undefined) queryObject.desc = desc;

    if (filters.name) queryObject.name = filters.name;
    if (filters.rut) queryObject.rut = filters.rut;
    if (filters.phone) queryObject.phone = filters.phone;
    if (filters.email) queryObject.email = filters.email;
    if (filters.officeId) queryObject.officeId = filters.officeId;
    if (filters.sacId) queryObject.sacId = filters.sacId;
    if (filters.startDate) queryObject.startDate = filters.startDate;
    if (filters.endDate) queryObject.endDate = filters.endDate;
    if (filters.category) queryObject.category = filters.category;

    const response = await api.get('/clients', {
      params: queryObject,
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
}

export async function exportClientsExcel(params, signal) {
  try {
    const { page, pageSize, q, state, ...filters } = params;

    const queryObject = {
      page: 1,
      pageSize: 10000,
    };

    if (q) queryObject.q = q;
    if (state === 'active') queryObject.active = true;
    else if (state === 'inactive') queryObject.active = false;

    if (filters.name) queryObject.name = filters.name;
    if (filters.rut) queryObject.rut = filters.rut;
    if (filters.phone) queryObject.phone = filters.phone;
    if (filters.email) queryObject.email = filters.email;
    if (filters.officeId) queryObject.officeId = filters.officeId;
    if (filters.sacId) queryObject.sacUserId = filters.sacId;
    if (filters.startDate) queryObject.startDate = filters.startDate;
    if (filters.endDate) queryObject.endDate = filters.endDate;
    if (filters.category) queryObject.category = filters.category;

    const response = await api.get('/clients/ExportExcel', {
      params: queryObject,
      responseType: 'blob',
      signal,
    });

    // Obtener el nombre del archivo desde el header Content-Disposition o usar uno por defecto
    const contentDisposition = response.headers['content-disposition'];
    let filename = `Clientes_${new Date().toISOString().split('T')[0]}.xlsx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Crear blob con el tipo MIME correcto
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Intentar usar showSaveFilePicker para mostrar diálogo de guardar (navegadores modernos)
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [
            {
              description: 'Archivo Excel',
              accept: {
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                  ['.xlsx'],
              },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch (pickerError) {
        // Si el usuario cancela el diálogo o hay error, usar método tradicional
        if (pickerError.name === 'AbortError') {
          return; // Usuario canceló
        }
        console.warn(
          'showSaveFilePicker no disponible, usando método alternativo'
        );
      }
    }

    // Método alternativo: crear link de descarga
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Limpiar después de un pequeño delay
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Error descargando Excel:', error);
    throw error;
  }
}

export async function getTranscripcionesPorCliente(
  telefonos,
  page = 1,
  pageSize = 10,
  fechaDesde = null,
  fechaHasta = null,
  signal,
  sortBy = null,
  desc = null
) {
  try {
    const payload = {
      telefonos,
      pageNumber: page,
      pageSize,
    };

    if (fechaDesde) payload.fechaDesde = fechaDesde;
    if (fechaHasta) payload.fechaHasta = fechaHasta;
    if (sortBy) payload.sortBy = sortBy;
    if (desc !== null && desc !== undefined) payload.desc = desc;

    const response = await api.post(
      '/transcripciones/GetTranscripcionesPorCliente',
      payload,
      { signal }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching transcripciones:', error);
    throw error;
  }
}

export async function getTranscripcionById(idTranscripcion, signal) {
  try {
    const response = await api.get('/transcripciones/GetTranscripcion', {
      params: { idTranscripcion },
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching transcripcion:', error);
    throw error;
  }
}

export async function getAudioTranscripcion(idTranscripcion) {
  try {
    const response = await api.post(
      '/transcripciones/DownloadAudioTranscripcion',
      null,
      {
        params: { idTranscripcion },
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error downloading audio:', error);
    throw error;
  }
}

export async function getContractsByClientId(clientId, signal) {
  try {
    const response = await api.get(`/clients/${clientId}/contracts`, {
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching contracts:', error);
    throw error;
  }
}

export async function getClientEmailsFromGraph(userEmail) {
  try {
    const response = await api.get('/Clients/microsoft-graph/emails', {
      params: {
        userEmail,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching client emails from Graph:', error);
    throw error;
  }
}

export async function getContractsPage(
  clientId,
  page = 1,
  pageSize = 20,
  signal,
  sortBy = null,
  desc = null
) {
  try {
    const params = {
      page,
      pageSize,
    };

    if (sortBy) params.sortBy = sortBy;
    if (desc !== null && desc !== undefined) params.desc = desc;

    const response = await api.get(`/clients/${clientId}/contracts/page`, {
      params,
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching contracts page:', error);
    throw error;
  }
}

export async function getTasksByBusinessArea(
  clientId,
  businessAreaName,
  signal,
  isOld = false
) {
  try {
    const response = await api.get(
      `/clients/${clientId}/tasks/by-business-area`,
      {
        params: {
          businessAreaName,
          isOld,
        },
        signal,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching tasks by business area:', error);
    throw error;
  }
}

export async function getContractsRepositoryPaged(
  clientId,
  {
    page = 1,
    pageSize = 20,
    search = null,
    startDate = null,
    endDate = null,
    sortBy = null,
    desc = null,
  } = {},
  signal
) {
  try {
    const params = { page, pageSize };

    if (search?.trim()) params.search = search.trim();

    if (startDate) params.startDate = toYMD(startDate);
    if (endDate) params.endDate = toYMD(endDate);

    if (sortBy) params.sortBy = sortBy;
    if (desc !== null && desc !== undefined) params.desc = desc;

    const response = await api.get(
      `/clients/${clientId}/contracts/repository`,
      { params, signal }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching contracts repository:', error);
    throw error;
  }
}
